import React, { memo, useCallback, useMemo, useState, useContext } from 'react';
import { Button, Col, Modal, Row, Select, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import SessionContext from '@providers/Session/SessionContext';
import api from '@services/api';
import SelectContextItem from '@components/SelectContext/SelectContextItem';
import { Label } from '@components/UI';
import IndividualRangePicker from '@components/DatePicker/IndividualRangePicker';

interface RequestCancelReportsProps {
  visible: boolean;
  onClose: () => void;
}

const RequestCancelReports = ({
  visible,
  onClose,
}: RequestCancelReportsProps) => {
  const { t, i18n } = useTranslation('invoices');
  const navigate = useNavigate();

  const [customerId, setCustomerId] = useState();
  const [requestDate, setRequestDate] = useState<{
    requestDateFrom: string | undefined;
    requestDateTo: string | undefined;
  }>();
  const [typeOfReport, setTypeOfReport] = useState<
    'report' | 'report-detailed'
  >('report');
  const [loadingRequest, setLoadingRequest] = useState(false);

  const { customer } = useContext(SessionContext);

  const requestReport = useCallback(
    () =>
      api.get(
        `/service-proxy/broker/cancellation/${typeOfReport}?locale=${i18n.language}`,
        {
          params: {
            customerId,
            ...requestDate,
          },
        },
      ),
    [typeOfReport, i18n.language, customerId, requestDate],
  );

  const handleClose = useCallback(() => {
    onClose();
    setCustomerId(undefined);
    setRequestDate(undefined);
    setTypeOfReport('report');
  }, [onClose, setTypeOfReport]);

  const navigateToReports = useCallback(() => {
    message.destroy('invoice-report');
    navigate(
      `/support/reports?type=cancellation_management${
        typeOfReport === 'report-detailed' ? '_detailed' : ''
      }`,
    );
  }, [navigate, typeOfReport]);

  const handleRequestReport = useCallback(async () => {
    try {
      setLoadingRequest(true);
      await requestReport();
      message.success({
        content: (
          <span>
            {t('report.msgs.request-success')}
            <Button
              onClick={navigateToReports}
              type="link"
              style={{ padding: 3 }}
            >
              {t('report.msgs.go-to')}
            </Button>
            {t('report.msgs.check-progress-and-download')}
          </span>
        ),
      });
      setTypeOfReport('report');
    } catch {
      message.error(t('report.msgs.check-progress-and-download'));
    }
    handleClose();
    setLoadingRequest(false);
  }, [t, navigateToReports, requestReport, handleClose]);

  const mapOptions = useCallback(
    (options: any) =>
      options?.map((item: any) => ({
        value: item.id,
        label: (
          <SelectContextItem id={item.id} cpfCnpj={item.cpfCnpj} {...item} />
        ),
        filterProps: `${item.name};${item.tradingName};${item.cpfCnpj};${item.id}`,
      })),
    [],
  );

  const modalInputs = useMemo(() => {
    const inputs = [
      {
        col: {
          span: 24,
        },
        label: t('search.customer'),
        item: (
          <Select
            value={customerId}
            onSelect={setCustomerId}
            style={{ width: '100%' }}
            options={mapOptions(customer?.customersData)}
            filterOption={(input, option) => {
              const verifyString = (str: string) =>
                String(str).toLowerCase().indexOf(input.toLowerCase()) >= 0;
              return verifyString(option?.filterProps);
            }}
            showSearch
            allowClear
          />
        ),
      },
      {
        col: {
          lg: 8,
          xs: 24,
        },
        label: t('search.type'),
        item: (
          <Select
            defaultValue="report"
            onChange={setTypeOfReport}
            value={typeOfReport}
            style={{ width: '100%' }}
            options={[
              { label: t('search.summarized'), value: 'report' },
              { label: t('search.detailed'), value: 'report-detailed' },
            ]}
          />
        ),
      },
      {
        col: {
          lg: 16,
          xs: 24,
        },
        label: t('search.date'),
        item: (
          <IndividualRangePicker
            onChange={({ startDate, endDate }) => {
              setRequestDate({
                requestDateFrom: startDate,
                requestDateTo: endDate,
              });
            }}
            value={[
              requestDate?.requestDateFrom ?? '',
              requestDate?.requestDateTo ?? '',
            ]}
          />
        ),
      },
    ];

    return inputs;
  }, [
    t,
    customerId,
    mapOptions,
    customer?.customersData,
    requestDate?.requestDateFrom,
    requestDate?.requestDateTo,
    typeOfReport,
  ]);

  return (
    <Modal
      bodyStyle={{
        paddingTop: 0,
      }}
      footer={
        <>
          <Button onClick={handleClose}>{t('report.buttons.cancel')}</Button>
          <Button
            type="primary"
            loading={loadingRequest}
            disabled={loadingRequest}
            onClick={handleRequestReport}
          >
            {t('report.buttons.request')}
          </Button>
        </>
      }
      maskClosable={false}
      okButtonProps={{ form: 'customerForm', htmlType: 'submit' }}
      style={{
        maxWidth: 520,
      }}
      destroyOnClose
      title={t('report.request')}
      open={visible}
      width="100%"
      centered
      onCancel={handleClose}
    >
      <Row gutter={[16, 16]} style={{ padding: 16 }}>
        {modalInputs?.map(({ col, label, item }) => (
          <Col key={label} {...col}>
            {label && (
              <div>
                <Label htmlFor={label}>{label}</Label>
              </div>
            )}
            {item}
          </Col>
        ))}
      </Row>
    </Modal>
  );
};

export default memo(RequestCancelReports);
